import React from 'react';
import { Box, ChevronDownIcon, ChevronUpIcon, LockIcon } from '@razorpay/blade/components';
import { ACCESS_LABELS, ACCESS_META } from '../../../constants';
import { EmployeeFieldAccess, EmployeeFieldAccessRole } from '../../../types';
import { AccessControlButton, AccessControlLabel, AccessMenu, AccessMenuOption } from '../../../styles';

type AccessControlProps = {
  role: EmployeeFieldAccessRole;
  currentAccess: EmployeeFieldAccess;
  isOpen: boolean;
  onToggle: (roleId: number) => void;
  onSelect: (roleId: number, access: EmployeeFieldAccess) => void;
};

export const AccessControl = ({
  role,
  currentAccess,
  isOpen,
  onToggle,
  onSelect,
}: AccessControlProps) => {
  const AccessIcon = ACCESS_META[currentAccess].Icon;

  return (
    <Box position="relative">
      <AccessControlButton
        type="button"
        $isDisabled={role.isAccessLocked}
        disabled={role.isAccessLocked}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${role.name} access ${ACCESS_LABELS[currentAccess]}`}
        onClick={() => onToggle(role.roleId)}>
        <AccessControlLabel>
          <AccessIcon size="medium" color="interactive.icon.gray.normal" />
          {/* Native span: inherits AccessControlButton's app-theme color (incl. its
              disabled switch); Blade Text only accepts Blade color tokens, which render
              a visibly different shade here. */}
          <span>{ACCESS_LABELS[currentAccess]}</span>
        </AccessControlLabel>
        {role.isAccessLocked ? (
          <LockIcon size="small" color="surface.icon.gray.muted" />
        ) : isOpen ? (
          <ChevronUpIcon size="medium" color="surface.icon.gray.muted" />
        ) : (
          <ChevronDownIcon size="medium" color="surface.icon.gray.muted" />
        )}
      </AccessControlButton>
      {isOpen && !role.isAccessLocked ? (
        <AccessMenu role="listbox" aria-label={`${role.name} access options`}>
          {(Object.entries(ACCESS_LABELS) as Array<[EmployeeFieldAccess, string]>).map(
            ([accessValue, accessLabel]) => {
              const OptionIcon = ACCESS_META[accessValue].Icon;

              return (
                <AccessMenuOption
                  key={accessValue}
                  type="button"
                  role="option"
                  aria-selected={currentAccess === accessValue}
                  $isSelected={currentAccess === accessValue}
                  onClick={() => onSelect(role.roleId, accessValue)}>
                  <OptionIcon size="medium" color="interactive.icon.gray.normal" />
                  {accessLabel}
                </AccessMenuOption>
              );
            },
          )}
        </AccessMenu>
      ) : null}
    </Box>
  );
};
